import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { ErrorMessage } from '@sdir/utilities/lib/components';
// eslint-disable-next-line max-len
import { HealthServiceCoreFeaturesMedicalExaminationApiModelsMedicalCertificateVerifyDto as HealthDto } from '@sdir/httpclient/lib/clients/aps/health';
import { BackLink, FormInput } from '@sdir/blueprint.aps/lib/components';
import { Loader, SdirButton } from '@sdir/sds';
import { medicalExaminationApi } from '../../../httpclient';
import { HealthDeclaration } from '../../Molecules';
import { routes } from '../../../constants/routes';
import { useNavigateWithLang } from '../../../services/hooks/useNavigateWithLang';

const SearchHealthPage: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigateWithLang();
  const [searchError, setSearchError] = useState<any>(null);
  const [health, setHealth] = useState<HealthDto>();
  const [loading, setLoading] = useState<boolean>(false);

  const initialValues = {
    documentNumber: '',
  };

  const handleSubmit = useCallback(
    (values) => {
      setLoading(true);
      medicalExaminationApi
        .v1MedicalcertificateGetmedicalcertificateverificationCertificateNumberGet(
          values?.documentNumber
        )
        .then((response) => {
          if (response.status === 200) {
            setHealth(response.data);
            setSearchError(undefined);
          }
          setLoading(false);
        })
        .catch((error) => {
          const { status } = error?.response || {};
          if (status >= 400 && status < 500) {
            setSearchError(intl.formatMessage({ id: 'health.notfound' }));
          } else {
            setSearchError(error);
          }
          setLoading(false);
        });
    },
    [intl]
  );

  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      documentNumber: Yup.string().required(intl.formatMessage({ id: 'required.field' })),
    });
  }, [intl]);

  return (
    <>
      <BackLink
        text={intl.formatMessage({ id: 'link.back.title' })}
        onClick={() => navigate(routes.home)}
      />
      <h1>{intl.formatMessage({ id: 'health.search.header' })}</h1>
      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {() => (
          <StyledForm>
            <Field
              label={intl.formatMessage({ id: 'health.number' })}
              name="documentNumber"
              component={FormInput}
            />
            <StyledSdirButton
              htmlType="submit"
              type="primary"
              text={intl.formatMessage({ id: 'search' })}
            />
            {loading && <Loader />}
            {searchError && <ErrorMessage error={searchError} />}
          </StyledForm>
        )}
      </Formik>
      {health && !searchError && (
        <ResultWrapper>
          <HealthDeclaration data={health} />
        </ResultWrapper>
      )}
    </>
  );
};

const StyledForm = styled(Form)`
  display: flex;
  grid-row-gap: 1em;
  flex-direction: column;
  max-width: 400px;
`;

const StyledSdirButton = styled(SdirButton)`
  width: fit-content;
`;

const ResultWrapper = styled.div`
  margin: 20px 0;

  p {
    margin: 0;
  }
`;

export default SearchHealthPage;
